import { html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';

import { getGlobalStyleSheets } from '../../global-styles';
import { enumAttributeConverter } from '../enum-types';

import { BannerTags, BannerEvents, BannerEnums, CookieBannerTypes, type AtlCookieBannerProps } from './banners.types.ts';
import { IconEnums, type AtlAriaIconProps } from '../icon';

import CookieBannerStyles from './cookie-banner.scss?inline';

@customElement(BannerTags.CookieBanner)
export class AtlCookieBanner
   extends LitElement
   implements AtlCookieBannerProps
{
   static styles = [
      ...getGlobalStyleSheets(),
      CookieBannerStyles
   ];

   @property({ attribute: 'banner-title', type: String })
   bannerTitle?: string;

   @property({ attribute: 'show-settings-button', type: Boolean })
   declare showSettingsButton?: boolean;

   @property({
      attribute: 'banner-type',
      type: String,
      converter: enumAttributeConverter(BannerEnums.CookieBannerTypes),
   })
   type?: CookieBannerTypes;

   @property({ attribute: 'button-label', type: String })
   buttonLabel?: string;

   @property({ type: Boolean, state: true })
   _accepted?: boolean;

   @query('.cookie-banner')
   private _cookieBanner?: HTMLElement;

   @query('.cookie-banner-overlay')
   private _cookieBannerOverlay?: HTMLElement;

   @query("button[data-type='accept-btn']")
   private _acceptButton?: HTMLButtonElement;

   override updated(changedProperties: PropertyValues) {
      super.updated(changedProperties);
      if (changedProperties.has('type')) {
         this._handleTypeChange();
      }
   }

   private _handleTypeChange() {
      const cookieBannerOverlay = this._cookieBannerOverlay;
      const cookieBanner = this._cookieBanner;

      if (this.type === CookieBannerTypes.PositionFixed) {
         this._acceptButton?.addEventListener(
            'blur',
            this._onBlurButtonHandler
         );
         cookieBannerOverlay?.classList?.add('fadeIn');
         cookieBanner?.classList?.add('fadeIn');

         setTimeout(function () {
            cookieBanner?.classList?.add('faddedIn');
            cookieBannerOverlay?.classList?.add('faddedIn');
         }, 100);
      } else {
         cookieBanner?.classList?.remove('fadeIn');
         cookieBanner?.classList?.remove('faddedIn');

         cookieBannerOverlay?.classList?.remove('fadeIn');
         cookieBannerOverlay?.classList?.remove('faddedIn');

         this._acceptButton?.removeEventListener(
            'blur',
            this._onBlurButtonHandler
         );
      }
   }

   private _onBlurButtonHandler = () => {
      this._acceptButton?.focus();
   };

   private _onAcceptHandler = () => {
      this.dispatchEvent(new BannerEvents.CookieButtonClickEvent());
      if (this.type === CookieBannerTypes.PositionFixed) {
         this._cookieBanner?.classList.remove('faddedIn');
         this._cookieBannerOverlay?.classList.remove('faddedIn');

         this._cookieBannerOverlay?.addEventListener(
            'transitionend',
            () => (this._accepted = true)
         );
      } else {
         this._cookieBanner?.classList.remove('show');
         this._cookieBanner?.classList.add('hide');

         this._cookieBanner?.addEventListener(
            'transitionend',
            () => (this._accepted = true)
         );
      }
   };

   render() {
      if (this._accepted) return nothing;

      const classNames = {
         'cookie-banner': true,
         'd-inline': !this.type || this.type === CookieBannerTypes.Inline,
         'position-fixed': this.type === CookieBannerTypes.PositionFixed,
         fade: this.type === CookieBannerTypes.Inline,
         show: this.type === CookieBannerTypes.Inline,
      };

      return html`
         <div
            class=${classMap(classNames)}
            role="alertdialog"
            tabindex="0"
            aria-label=${ifDefined(this.bannerTitle)}
         >
            <div class="banner-container">
               <div class="banner-body-container">
                  <atlantis-icon
                     iconSource=${IconEnums.IconSource.FontAwesome}
                     iconSize=${IconEnums.IconSizes.Zoom3x}
                     name="cookie-bite"
                     iconClassName="banner-icon"
                     .icon-aria-settings=${{
                        ariaHidden: true,
                     } as AtlAriaIconProps}
                  >
                  </atlantis-icon>
                  <div class="banner-body">
                     <div>
                        ${this.bannerTitle
                           ? html`<div class="h5 banner-title">${this.bannerTitle}</div>`
                           : nothing}
                        <div class="banner-text">
                           <slot></slot>
                        </div>
                     </div>
                  </div>
               </div>
               <div class="banner-cta-container">
                  ${this.showSettingsButton
                     ? html`<button
                          type="button"
                          class="btn btn-link banner-cta"
                          @click=${() =>
                             this.dispatchEvent(
                                new BannerEvents.CookieSettingsButtonClickEvent()
                             )}
                       >
                          Cookie Settings
                       </button>`
                     : nothing}
                  <button
                     type="submit"
                     data-type="accept-btn"
                     class="btn btn-primary btn-lg banner-cta"
                     @click=${this._onAcceptHandler}
                  >
                     ${this.buttonLabel || 'Accept'}
                  </button>
               </div>
            </div>
         </div>
         ${this.type === CookieBannerTypes.PositionFixed
            ? html`<div class="cookie-banner-overlay"></div>`
            : nothing}
      `;
   }
}
